import { faArrowRight } from "@fortawesome/pro-light-svg-icons/faArrowRight";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Prisma } from "@prisma/client";
import clsx from "clsx";
import { GetServerSideProps, InferGetServerSidePropsType } from "next";
import { getServerSession } from "next-auth/next";
import Image from "next/image";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useLocale, useTranslations } from "use-intl";
import { useEffectOnce } from "usehooks-ts";
import pkg from "../../package.json";
import WelcomeImage from "../../public/calculatorWelcome.png";
import Button from "../../src/components/Button/Button";
import CalculatorLeftArea from "../../src/components/Co2Calculator/CalculatorLeftArea/CalculatorLeftArea";
import CalculatorSummary from "../../src/components/Co2Calculator/CalculatorSummary/CalculatorSummary";
import QuestionLayout from "../../src/components/Co2Calculator/QuestionLayout";
import QuestionNavigator from "../../src/components/Co2Calculator/QuestionNavigator/QuestionNavigator";
import SkeletonImage from "../../src/components/image/SkeletonImage";
import { useAuthContext } from "../../src/context/AuthContext";
import { useLayoutContext } from "../../src/context/LayoutContext";
import useSessionUser from "../../src/hooks/useSessionUser";
import {
  calculatorActions,
  CalculatorStoreState,
} from "../../src/redux/co2calculatorSlice";
import { useAppDispatch, useAppSelector } from "../../src/redux/hooks";
import {
  CalculatorQuestionQueryId,
  CookieCalculatorQuestionSessionKey,
} from "../../src/util";
import { getMessages } from "../../src/util/messages";
import prisma from "../../src/util/prisma";
import { TSelectCurrentQuestion } from "../../src/util/prismaHelper";
import { HGFPage } from "../../types/util";
import { authOptions } from "../api/auth/[...nextauth]";
import styles from "./Co2CalculatorIndex.module.scss";

const QuestionSessionInclude =
  Prisma.validator<Prisma.CalculatorQuestionSessionArgs>()({
    include: {
      currentQuestion: true,
    },
  });

type QuestionSessionInclude = Prisma.CalculatorQuestionSessionGetPayload<
  typeof QuestionSessionInclude
>;

function expires() {
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  return expires;
}

export const getServerSideProps: GetServerSideProps<{
  settings: {
    isIFrame: boolean;
  };
}> = async ({ query, req, res, locale }) => {
  let calculatorSession: QuestionSessionInclude | null = null;
  const queryId = query[CalculatorQuestionQueryId];
  const resetSession = "r" in query;
  console.log("[CO2-Calc] Query", query, queryId, resetSession);
  if (!resetSession) {
    if (queryId && typeof queryId === "string") {
      calculatorSession = await prisma.calculatorQuestionSession.findUnique({
        where: {
          id: queryId,
        },
        include: {
          currentQuestion: true,
        },
      });
      console.log(
        "[CO2-Calc] Checking session by queryId",
        queryId,
        calculatorSession?.id
      );
      if (calculatorSession) {
        res.setHeader(
          "Set-Cookie",
          `${CookieCalculatorQuestionSessionKey}=${
            calculatorSession.id
          }; Path=/; HttpOnly; Expires=${expires().toUTCString()}; ${
            process.env.NODE_ENV === "production"
              ? "Secure; SameSite=None;Partitioned;"
              : ""
          }`
        );
      }
    } else if (CookieCalculatorQuestionSessionKey in req.cookies) {
      calculatorSession = await prisma.calculatorQuestionSession.findUnique({
        where: {
          id: req.cookies[CookieCalculatorQuestionSessionKey],
        },
        include: {
          currentQuestion: true,
        },
      });
    }
  }

  let userId = null;
  try {
    const session = await getServerSession(req, res, authOptions(req, res));
    if (session?.user?.id) {
      userId = session.user.id;
    }
  } catch (e) {}

  if (!calculatorSession) {
    const questionCount = await prisma.calculatorQuestion.count();
    calculatorSession = await prisma.calculatorQuestionSession.create({
      data: {
        currentQuestionId: null,
        // currentQuestionId: "transport@5",
        // currentQuestionId: "culture@1",
        // currentQuestionId: "pet@1",
        data: {},
        answers: {},
        summary: {},
        questionCount: questionCount,
        currentQuestionNumber: 0,
        skippedQuestionIds: [],
        userId: userId,
        version: pkg.version,
      },
      include: {
        currentQuestion: true,
      },
    });

    // FIXME: move session to localstorage & auth header
    //  cookies are not a good option for Iframes
    res.setHeader(
      "Set-Cookie",
      `${CookieCalculatorQuestionSessionKey}=${
        calculatorSession.id
      }; Path=/; HttpOnly; Expires=${expires().toUTCString()}; ${
        process.env.NODE_ENV === "production"
          ? "Secure; SameSite=None;Partitioned;"
          : ""
      }`
    );
  } else {
    if (calculatorSession.userId == null && userId != null) {
      await prisma.calculatorQuestionSession.update({
        where: {
          id: calculatorSession.id,
        },
        data: {
          userId: userId,
        },
      });
    }
  }

  const secDest = req.headers["sec-fetch-dest"];
  // const secMode = req.headers["sec-fetch-mode"];
  // const secSite = req.headers["sec-fetch-site"];

  const settings = {
    isIFrame: false,
  };

  if (secDest === "iframe" || secDest === "frame" || secDest === "embedded") {
    settings.isIFrame = true;
  }

  return {
    props: {
      messages: await getMessages(Co2CalculatorIndexPage.Messages, locale),
      settings: settings,
    },
  };
};

export type Co2CalculatorIndexPageProps = InferGetServerSidePropsType<
  typeof getServerSideProps
>;

async function loadQuestion(id: null | undefined | string) {
  if (!id) return null;
  const resp = await fetch(`/api/co2-calculator/q/${id}`);
  if (resp.ok) {
    return (await resp.json()) as TSelectCurrentQuestion & {
      meta: { userAnswer: CalculatorStoreState["answers"][string] };
    };
  } else {
    console.error("No question found");
    return null;
  }
}

function nonEmptyAnswers(a: CalculatorStoreState["answers"][string]) {
  return (
    a != null &&
    (typeof a.value === "string" ||
      typeof a.value === "number" ||
      Object.keys(a).length > 0)
  );
}

const Co2CalculatorIndexPage: HGFPage<Co2CalculatorIndexPageProps> = ({
  settings,
}: Co2CalculatorIndexPageProps) => {
  const l = useLocale();
  const state = useAppSelector((s) => s.calculator);
  const t = useTranslations("Co2CalculatorIndexPage");
  const currentQuestion = state.currentQuestion;
  const dispatch = useAppDispatch();
  const [isQuestionLoading, setIsQuestionLoading] = useState(false);
  const { sessionStatus } = useSessionUser();
  const { update: updateLayoutConfig } = useLayoutContext();
  const { updateCallback } = useAuthContext();
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  if ("r" in router.query) {
    router.replace("/co2-calculator");
  }

  const qTitle = currentQuestion?.QuestionTranslation.find(
    (qt) => qt.key === "title" && qt.language === l
  );

  const qHint = currentQuestion?.QuestionTranslation.find(
    (qt) => qt.key === "hint" && qt.language === l
  );

  const onNavigation = () => {
    const scroll = () => {
      rightContainerRef.current?.scrollTo({
        left: 0,
        top: 0,
        behavior: "auto",
      });
    };
    setTimeout(scroll, 133);
  };

  useEffectOnce(() => {
    if (settings.isIFrame) {
      updateLayoutConfig("hideHeader", true);
      updateLayoutConfig("hideFooter", true);
      updateLayoutConfig(
        "containerClassName",
        clsx(styles.app_c, styles.iframe)
      );
    }
  });

  useEffect(() => {
    updateCallback("logout", "/co2-calculator");
    updateCallback("login", "/co2-calculator");
  }, [updateCallback]);

  useEffect(() => {
    if (sessionStatus === "authenticated") {
      updateLayoutConfig("hideNavigation", false);
    } else {
      updateLayoutConfig("hideNavigation", true);
    }
  }, [updateLayoutConfig, sessionStatus]);

  useEffect(() => {
    if (!state.session?.currentQuestionId) return;
    setIsQuestionLoading(true);
    loadQuestion(state.session.currentQuestionId)
      .then((json) => {
        if (json) {
          dispatch(calculatorActions.setQuestion(json));
          dispatch(calculatorActions.setPQId(json.previousQuestionId || null));
          if (
            nonEmptyAnswers(state.answers[json.id]) ||
            json.meta.userAnswer != null ||
            json.allowEmpty
          ) {
            dispatch(calculatorActions.setNQId(json.nextQuestionId || null));
          } else {
            dispatch(calculatorActions.setNQId(null));
          }
        } else {
          dispatch(calculatorActions.setNQId(null));
        }
      })
      .finally(() => {
        setIsQuestionLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.session?.currentQuestionId]);

  let content = (
    <>
      <div className={styles.left}>
        <CalculatorLeftArea
          category={
            currentQuestion
              ? t(
                  `category.${
                    currentQuestion.category.toLowerCase() as keyof IntlMessages["Co2CalculatorIndexPage"]["category"]
                  }`
                )
              : ""
          }
        />
      </div>
      <div className={styles.right} ref={rightContainerRef}>
        <div
          className={clsx(
            styles.rightContent,
            isQuestionLoading && styles.isQuestionLoading
          )}
        >
          {currentQuestion ? (
            <QuestionLayout
              questionId={currentQuestion.id}
              question={qTitle?.value ?? currentQuestion.question}
              answers={currentQuestion.QAnswer}
              questionHint={qHint?.value ?? currentQuestion.hint}
            />
          ) : (
            <SkeletonImage className={styles.skele} />
          )}
        </div>
        <QuestionNavigator onNavigate={onNavigation} />
      </div>
    </>
  );

  if (!currentQuestion) {
    content = (
      <>
        <div className={styles.left}>
          <CalculatorLeftArea category={t("welcome")} />
        </div>
        <div className={clsx(styles.right, styles.start)}>
          <div className={styles.welcomeContainer}>
            <Image
              src={WelcomeImage.src}
              width={203}
              height={203}
              alt={t("welcomeHeader")}
              className={styles.logo}
            />
            <div className={styles.title}>{t("welcomeHeader")}</div>
            <div className={styles.content}>{t("welcomeContent")}</div>
            <Button
              className={styles.startButton}
              color="green"
              onClick={() => {
                dispatch(calculatorActions.setNQId("country@1"));
                dispatch(calculatorActions.nextQuestion());
              }}
            >
              {t("startCalculator")}
              <FontAwesomeIcon size="lg" icon={faArrowRight} />
            </Button>
          </div>
        </div>
      </>
    );
  } else if (currentQuestion?.id === "summary@1") {
    content = (
      <div className={clsx(styles.right, styles.summary)}>
        <CalculatorSummary />
      </div>
    );
  }

  return (
    <>
      <div className={styles.container}>
        {content}
        {process.env.NODE_ENV === "development" && (
          <div
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              padding: "5px 8px",
              color: "#ffffff",
              borderRadius: "8px",
              zIndex: 1000,
              background: "red",
              cursor: "pointer",
            }}
            onClick={async () => {
              const resp = await fetch("/api/co2-calculator/s/storage", {
                method: "DELETE",
              });
              if (resp.ok) {
                router.reload();
              } else {
                toast.error("[DEV] problem bei löschen der Session");
              }
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </div>
        )}
      </div>
    </>
  );
};

Co2CalculatorIndexPage.Messages = ["Co2CalculatorIndexPage"];

Co2CalculatorIndexPage.Layout = {
  hideNavigation: true,
  hideHeader: false,
  mainClassName: styles.main,
  containerClassName: styles.app_c,
};
export default Co2CalculatorIndexPage;
